import { Injectable } from '@angular/core';
import { UserService } from '../user/user.service';
import { IEmailAddress, IMyDetails, INotificationPreferences, IPhoneNumber } from '../../interfaces/interfaces';
import { MyDetailsEvents } from '../../mydetails/mydetails.events';

import { Log } from '../../shared/log';
import { UserEvents } from '../user/user.events';
import * as moment from 'moment';
import { AppConfig } from '../../app.config';


const log = new Log('ProfileService');

@Injectable()
export class ProfileService {
  private savedProfile: IMyDetails;
  private seniorsThresholdAge = this.appConfig.getValue('seniorsThresholdAge');
  private notificationPreferences: INotificationPreferences;
  private preferredEmail: IEmailAddress;
  private preferredPhoneNumber: IPhoneNumber;

  constructor(
    private userService: UserService,
    private myDetailsEvents: MyDetailsEvents,
    private userEvents: UserEvents,
    private appConfig: AppConfig
  ) {
    myDetailsEvents.postcodeSaved.subscribe(this.updateProfile.bind(this));
    myDetailsEvents.ageSaved.subscribe(this.updateProfile.bind(this));
    myDetailsEvents.namesSaved.subscribe(this.updateProfile.bind(this));
    myDetailsEvents.genderSaved.subscribe(this.updateProfile.bind(this));
    myDetailsEvents.addressSaved.subscribe(this.updateProfile.bind(this));
    myDetailsEvents.subscribedServicesSaved.subscribe(this.updateProfile.bind(this));
    myDetailsEvents.notificationPreferencesSaved.subscribe(this.updateNotificationPreferences.bind(this));
    myDetailsEvents.preferredEmailSaved.subscribe(this.updatePreferredEmail.bind(this));
    myDetailsEvents.preferredPhoneNumberSaved.subscribe(this.updatePreferredPhoneNumber.bind(this));
    userEvents.vehicleRegistrationChanged.subscribe(this.onVehicleRegistrationChanged.bind(this));
  }

  getSavedProfile(): Promise<IMyDetails> {
    if (this.savedProfile) {
      return Promise.resolve(this.savedProfile);
    }

    return this.updateProfile();
  }

  updateProfile(): Promise<IMyDetails> {
    return this.userService.getMyDetails(2)
      .then(profile => {
        if (profile) {
          let age = profile.contactAge.age;
          if (profile.birthday) {
            age = moment().diff(profile.birthday, 'years');
          }
          if (age && age >= this.seniorsThresholdAge) {
            profile.isSenior = true;
          } else {
            profile.isSenior = false;
          }
          this.savedProfile = profile;
          return profile;
        }
      })
      .catch((error) => {
        log.error('Failed to get user profile', error);
        return Promise.reject(error);
      });
  }

  removeProfile(): void {
    this.savedProfile = null;
  }

  getEmailAddress(): string {
    return this.savedProfile.emailAddress;
  }

  getPreferredOrLoginEmailAddress(): Promise<string> {
    return Promise.all([this.getPreferredEmail(), this.getSavedProfile()])
      .then(([preferredEmailResponse, savedProfileResponse]) => {
        if (preferredEmailResponse && preferredEmailResponse.email_address) {
          return preferredEmailResponse.email_address;
        } else {
          return savedProfileResponse.emailAddress;
        }
      })
      .catch(error => {
        log.error('Failed to get preferred email or user profile', error);
        return Promise.reject(error);
      });
  }

  getPreferredEmail(): Promise<IEmailAddress> {
    if (this.preferredEmail) {
      return Promise.resolve(this.preferredEmail)
    }
    return this.updatePreferredEmail();
  }

  updatePreferredEmail(): Promise<IEmailAddress> {
    return this.userService.getEmailAddress()
      .then(email => {
        this.preferredEmail = email;
        return email
      })
      .catch((error) => {
        log.error('Failed to get preferred email', error);
        return Promise.reject(error);
      });
  }

  getPreferredPhoneNumber(): Promise<IPhoneNumber> {
    if (this.preferredPhoneNumber) {
      return Promise.resolve(this.preferredPhoneNumber);
    }
    return this.updatePreferredPhoneNumber();
  }

  updatePreferredPhoneNumber(): Promise<IPhoneNumber> {
    return this.userService.getPreferredPhoneNumber()
      .then(phoneNumber => {
        this.preferredPhoneNumber = phoneNumber;
        return phoneNumber
      })
      .catch((error) => {
        log.error('Failed to get phone number', error);
        return Promise.reject(error);
      });
  }

  getLevel(): number {
    return this.savedProfile.level;
  }

  getNotificationPreferences(): Promise<INotificationPreferences> {
    if (this.notificationPreferences) {
      return Promise.resolve(this.notificationPreferences);
    }

    return this.updateNotificationPreferences();
  }

  updateNotificationPreferences(): Promise<INotificationPreferences> {
    return this.userService.getNotificationPreferences()
      .then(newNotificationPreferences => {
        this.notificationPreferences = newNotificationPreferences;
        return newNotificationPreferences
      })
      .catch((error) => {
        log.error('Failed to get notification preferences', error);
        return Promise.reject(error);
      });
  }

  onVehicleRegistrationChanged() {
    log.warn('onVehicleRegistrationChanged', 'NOT IMPLEMENTED');
    this.updateProfile();
  }
}



// WEBPACK FOOTER //
// ./app/services/profile/profile.service.ts